'use client';

import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useReducer } from 'react';
import noop from 'lodash/noop';
import { createContext, useContextSelector } from 'use-context-selector';

const defaultState = {
  cookieConsentLastUpdate: 0,
  isSegmentLoaded: false,
  isUserIdentified: false,
  setUserIdentified: noop,
  toggleSegmentLoaded: noop,
  updateCookieConsentLastUpdate: noop,
};

type SegmentAction =
  | { type: 'TOGGLE_SEGMENT_LOADED' }
  | { type: 'SET_USER_IDENTIFIED'; value: boolean }
  | { type: 'UPDATE_COOKIE_CONSENT_LAST_UPDATE' };

type SegmentState = {
  cookieConsentLastUpdate: number;
  isSegmentLoaded: boolean;
  isUserIdentified: boolean;
  setUserIdentified: (value: boolean) => void;
  toggleSegmentLoaded: () => void;
  updateCookieConsentLastUpdate: () => void;
};

const segmentReducer = (state: SegmentState, action: SegmentAction) => {
  if (action.type === 'TOGGLE_SEGMENT_LOADED') {
    return { ...state, isSegmentLoaded: !state.isSegmentLoaded };
  }

  if (action.type === 'SET_USER_IDENTIFIED') {
    return { ...state, isUserIdentified: action.value };
  }

  if (action.type === 'UPDATE_COOKIE_CONSENT_LAST_UPDATE') {
    return { ...state, cookieConsentLastUpdate: Date.now() };
  }

  return state;
};

export const SegmentContext = createContext(defaultState);

export const SegmentContextProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(segmentReducer, defaultState);

  const toggleSegmentLoaded = useCallback(
    () => dispatch({ type: 'TOGGLE_SEGMENT_LOADED' }),
    []
  );
  const setUserIdentified = useCallback(
    (value: boolean) => dispatch({ type: 'SET_USER_IDENTIFIED', value }),
    []
  );
  const updateCookieConsentLastUpdate = useCallback(
    () => dispatch({ type: 'UPDATE_COOKIE_CONSENT_LAST_UPDATE' }),
    []
  );

  const { cookieConsentLastUpdate, isSegmentLoaded, isUserIdentified } = state;

  const value = useMemo(
    () => ({
      cookieConsentLastUpdate,
      isSegmentLoaded,
      isUserIdentified,
      setUserIdentified,
      toggleSegmentLoaded,
      updateCookieConsentLastUpdate,
    }),
    [
      cookieConsentLastUpdate,
      isSegmentLoaded,
      isUserIdentified,
      setUserIdentified,
      toggleSegmentLoaded,
      updateCookieConsentLastUpdate,
    ]
  );

  return (
    <SegmentContext.Provider value={value}>{children}</SegmentContext.Provider>
  );
};

export const useIsUserIdentified = () => {
  const isUserIdentified = useContextSelector(
    SegmentContext,
    (value) => value?.isUserIdentified
  );
  const setUserIdentified = useContextSelector(
    SegmentContext,
    (value) => value?.setUserIdentified
  );

  return {
    isUserIdentified,
    setUserIdentified,
  };
};

export const useIsSegmentLoaded = () => {
  const isSegmentLoaded = useContextSelector(
    SegmentContext,
    (value) => value?.isSegmentLoaded
  );
  const toggleSegmentLoaded = useContextSelector(
    SegmentContext,
    (value) => value?.toggleSegmentLoaded
  );

  return {
    isSegmentLoaded,
    toggleSegmentLoaded,
  };
};

export const useCookieConsentLastUpdate = () => {
  const cookieConsentLastUpdate = useContextSelector(
    SegmentContext,
    (value) => value?.cookieConsentLastUpdate
  );
  const updateCookieConsentLastUpdate = useContextSelector(
    SegmentContext,
    (value) => value?.updateCookieConsentLastUpdate
  );

  return {
    cookieConsentLastUpdate,
    updateCookieConsentLastUpdate,
  };
};
